/**
 * @file StandingOrderArchivedDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description "Create Customer Order" on a standing order whose product is
 *              archived (Cam, 15 Oct 2026). No order is made: the dialog says
 *              which product is archived and asks the person to find out from
 *              the customer (or their rep) what replaces it, then change the
 *              standing order — Edit standing order opens it with the product
 *              picker highlighted. Active alternatives on the standing order
 *              are listed as candidates; nothing is swapped in for them.
 *              The words are utils/standingOrderProducts (the order save's
 *              409 says the same).
 */

import React from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { productLabel } from '../utils/standingOrderProducts';
import './StandingOrders.css';

/* The footer mounts with the modal's portal, so its slide-out buttons are
   measured when they exist (see CLAUDE.md, useSlideOutButton). */
function ArchivedDialogFooter({ onEdit, onClose }) {
  useSlideOutButton([]);
  return (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" data-read-safe onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout btn-slideout-primary" onClick={onEdit}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
          <span className="btn-label">Edit standing order</span>
        </button>
      </div>
    </div>
  );
}

/**
 * @param {Object|null} prompt { order, block } — block from createOrderBlock
 * @param {Function} onEdit   (order) => open the standing order's edit window
 * @param {Function} onClose
 */
function StandingOrderArchivedDialog({ prompt, onEdit, onClose }) {
  if (!prompt) return null;
  const { order, block } = prompt;
  const customer = order.customer_name || order.customer_code || 'this customer';
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Product archived"
      size="sm"
      className="so-archived-prompt"
      footer={<ArchivedDialogFooter onEdit={() => onEdit(order)} onClose={onClose} />}
    >
      <div className="so-archived-prompt-message">
        <span className="material-symbols-rounded">warning</span>
        <p>{block.message}</p>
      </div>
      {block.fromLines && (
        <p className="so-archived-prompt-note">
          These are lines picked from {customer}'s purchase orders. Untick them, or change the standing order's product.
        </p>
      )}
      {block.candidates.length > 0 && (
        <div className="so-archived-prompt-candidates">
          <p>This standing order also lists:</p>
          <ul>
            {block.candidates.map(p => <li key={p.item_id || productLabel(p)}>{productLabel(p)}</li>)}
          </ul>
          <p className="so-archived-prompt-note">
            If one of these replaces it, make it the main product in Edit — check with the customer first.
          </p>
        </div>
      )}
    </Modal>
  );
}

export default StandingOrderArchivedDialog;
